import { zodResolver } from '@hookform/resolvers/zod';
import {
  cn,
  Field,
  FieldContent,
  FieldLabel,
  FieldTitle,
  Flex,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  RadioGroup,
  RadioGroupItem,
} from '@kognitos/lattice';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import i18n from '@/shared/i18n';

export interface ConnectionMethodStepData {
  connectionName: string;
  useCredentials: boolean;
}

export interface IConnectionMethodStepProps {
  existingConnectionNames?: string[];
  defaultValues?: Partial<ConnectionMethodStepData>;
  onChange: (data: ConnectionMethodStepData) => void;
  onSubmit: (data: ConnectionMethodStepData) => void;
}

const DEFAULT_EXISTING_CONNECTION_NAMES: string[] = [];

const createConnectionMethodStepSchema = (
  existingConnectionNames: string[],
) => {
  const requiredMsg = i18n.t(
    'integrations.addConnection.validation.connectionName.required',
    { ns: 'integrations' },
  );
  const uniqueMsg = i18n.t(
    'integrations.addConnection.validation.connectionName.unique',
    { ns: 'integrations' },
  );
  return z
    .object({
      connectionName: z.string(),
      useCredentials: z.boolean(),
    })
    .superRefine((data, ctx) => {
      if (!data.useCredentials) {
        if (!data.connectionName.trim()) {
          ctx.addIssue({
            path: ['connectionName'],
            code: 'custom',
            message: requiredMsg,
          });
        } else if (existingConnectionNames.includes(data.connectionName)) {
          ctx.addIssue({
            path: ['connectionName'],
            code: 'custom',
            message: uniqueMsg,
          });
        }
      }
    });
};

/**
 * Pre-step shown when an integration supports both credential-based and
 * credential-free connections (`connection_required === false` with auth methods
 * present). Lets the user name the connection and choose how to connect before
 * the auth-method selector or connection creation happens.
 */
export function ConnectionMethodStep({
  existingConnectionNames = DEFAULT_EXISTING_CONNECTION_NAMES,
  defaultValues,
  onChange,
  onSubmit,
}: IConnectionMethodStepProps) {
  const { t: tIntegrations } = useTranslation('integrations');

  const schema = useMemo(
    () => createConnectionMethodStepSchema(existingConnectionNames),
    [existingConnectionNames],
  );

  const form = useForm<ConnectionMethodStepData>({
    resolver: zodResolver(schema),
    defaultValues: {
      connectionName: defaultValues?.connectionName ?? '',
      useCredentials: defaultValues?.useCredentials ?? false,
    },
  });

  /**
   * Notifies the parent of the latest step data. Called from the field change
   * handlers so the parent stays in sync without a state-syncing effect.
   */
  const emitChange = (changed: Partial<ConnectionMethodStepData>) => {
    const current = form.getValues();
    onChange({
      connectionName: changed.connectionName ?? current.connectionName ?? '',
      useCredentials: changed.useCredentials ?? current.useCredentials,
    });
  };

  const handleSubmit = form.handleSubmit((data: ConnectionMethodStepData) => {
    onSubmit(data);
  });

  const radioItemClassName = cn(
    'cursor-pointer transition-colors duration-200 !rounded-lg',
  );

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit} id="connection-method-form">
        <Flex direction="column" align="stretch" gap={6} width="full">
          <FormField
            control={form.control}
            name="connectionName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {tIntegrations(
                    'integrations.addConnection.connectionName.label',
                  )}
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder={tIntegrations(
                      'integrations.addConnection.connectionName.placeholder',
                    )}
                    className="w-full"
                    {...field}
                    onChange={event => {
                      field.onChange(event);
                      emitChange({ connectionName: event.target.value });
                    }}
                  />
                </FormControl>
                <FormDescription>
                  {tIntegrations(
                    'integrations.addConnection.connectionName.description',
                  )}
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="useCredentials"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <RadioGroup
                    value={field.value ? 'true' : 'false'}
                    onValueChange={val => {
                      const useCredentials = val === 'true';
                      field.onChange(useCredentials);
                      emitChange({ useCredentials });
                    }}
                    className="flex flex-col gap-2"
                  >
                    <FieldLabel
                      htmlFor="connection-method-with-credentials"
                      className={radioItemClassName}
                    >
                      <Field
                        orientation="horizontal"
                        className="!p-3 !items-center gap-2"
                      >
                        <RadioGroupItem
                          value="true"
                          id="connection-method-with-credentials"
                          className="cursor-pointer"
                        />
                        <FieldContent>
                          <FieldTitle className="!font-normal !leading-none">
                            {tIntegrations(
                              'integrations.addConnection.connectionMethod.withCredentials',
                            )}
                          </FieldTitle>
                        </FieldContent>
                      </Field>
                    </FieldLabel>

                    <FieldLabel
                      htmlFor="connection-method-without-credentials"
                      className={radioItemClassName}
                    >
                      <Field
                        orientation="horizontal"
                        className="!p-3 !items-center gap-2"
                      >
                        <RadioGroupItem
                          value="false"
                          id="connection-method-without-credentials"
                          data-testid="connection-method-without-credentials"
                          className="cursor-pointer"
                        />
                        <FieldContent>
                          <FieldTitle className="!font-normal !leading-none">
                            {tIntegrations(
                              'integrations.addConnection.connectionMethod.withoutCredentials',
                            )}
                          </FieldTitle>
                        </FieldContent>
                      </Field>
                    </FieldLabel>
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </Flex>
      </form>
    </Form>
  );
}
